import { CountryLocaleMap, type CountryCode } from '@constants'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiApplePayMerchantValidation } from '@hooks-api/fortune/useApiApplePayMerchantValidation'
import { useApiCampaignActivityInfoImmutable } from '@hooks-api/fortune/useApiCampaignActivityInfoImmutable'
import { useApiFpCretePaymentTransaction, type UseApiFpCretePaymentTransactionProps } from '@hooks-api/fortune/useApiFpCretePaymentTransaction'
import { useApiFpPaymentExecution } from '@hooks-api/fortune/useApiFpPaymentExecution'
import { useApiGetGuestPaymentTransactionRequest } from '@hooks-api/fortune/useApiGetGuestPaymentTransactionRequest'
import { useApiUpdatePaymentTransactionInvoice } from '@hooks-api/fortune/useApiUpdatePaymentTransactionInvoice'
import { useApiFpPayment } from '@hooks-api/fortune-fastpass/useApiFpPayment'
import { type CreatePaymentTransactionsRequest } from '@hooks-api/types/apiRequest'
import { ECommerceOrderType, type ECommercePaymentTransactionsStatus, type UserPaymentTokenInfo } from '@hooks-api/types/dataModel/eCommercePayments'
import { PaymentServiceType } from '@hooks-api/types/dataModel/payment'
import { type ShippingMethod, SHIPPING_METHOD_TYPE } from '@hooks-api/types/dataModel/shipping'
import { PAYMENT_EXECUTION_MODE } from '@payments/lib/checkout-pay/BaseCheckoutPayFlowModule'
import { CheckoutPayCreditCardModule } from '@payments/lib/checkout-pay/CheckoutPayCreditCardModule'
import { isCkoFlowModule } from '@payments/lib/checkout-pay/isCkoFlowModule'
import { getActivePaymentModuleB2B } from '@payments/lib/getActivePaymentModule'
import { isPayMeUrl } from '@payments/lib/is-payme-url'
import { requiresPaymentModule } from '@payments/lib/paymentModuleMap'
import { TappayApplePayModule } from '@payments/lib/TappayApplePayModule'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom-v5'
import { create } from 'zustand'
import * as paymentService from '../core/paymentService'
import * as paymentTransactionService from '../core/paymentTransactionService'
import { CreatePaymentTransactionDTO } from '../dtos/fpCreatePaymentTransactionDTO'
import { getDefaultPaymentMethod } from '../utils/get-default-payment-method'
import { useFpCampaignCartCalculate } from './useFpCampaignCartCalculate'
import { useFpCampaignCartService } from './useFpCampaignCartService'
import { defaultCartValues, useFpCampaignCartStore } from './useFpCampaignCartStore'

export const getCvsStoreType = (appliedShippingMethod?: ShippingMethod) => {
  if (appliedShippingMethod?.shipping_method_type === SHIPPING_METHOD_TYPE.CVS_711) return SHIPPING_METHOD_TYPE.CVS_711
  if (appliedShippingMethod?.shipping_method_type === SHIPPING_METHOD_TYPE.CVS_FAMILY) return SHIPPING_METHOD_TYPE.CVS_FAMILY
  return undefined
}

interface ProcessOrderPaymentArgs {
  payment_success_url: (orderHash: string) => string
  payment_fallback_url: (orderHash: string) => string
  headers?: CreatePaymentTransactionsRequest['headers']
}
interface ExecutePaymentProcessArgs extends ProcessOrderPaymentArgs {
  orderHash: string
  orderId?: string
}
interface ProcessPendingOrderPaymentArgs extends ExecutePaymentProcessArgs {
  orderId: string
}

interface UseFpCampaignPaymentServiceArgs {
  campaignId: number | null
  campaignCode?: string
  orderType?: ECommerceOrderType
  orderId?: string
  orderHash?: string | null
  onCreatePaymentTransactionError?: (error: UseApiFpCretePaymentTransactionProps) => void
  /** Flow SDK init 必填（對齊 B2C `useWpPaymentService`）。Init 階段用 placeholder URL；Exec API 才會帶含 transactionId 的真實 URL。 */
  paymentSuccessUrl?: string
  paymentFallbackUrl?: string
  countryCode?: CountryCode
}

/**
 * SDK 整個 app 只會初始化一次，所以使用 store 來判斷是否已經初始化
*/
export const useFpCampaignPaymentServiceStore = create<{
  isPaymentProcessing: boolean
  isSdkInitialized: boolean
  isSdkInitializing: boolean
  payMePaymentQRCodeUrl: string | null
  setIsPaymentProcessing: (isPaymentProcessing: boolean) => void
  setIsSdkInitialized: (isSdkInitialized: boolean) => void
  setIsSdkInitializing: (isSdkInitializing: boolean) => void
  setPayMePaymentQRCodeUrl: (payMePaymentQRCodeUrl: string | null) => void
}>()(set => ({
  isPaymentProcessing: false,
  isSdkInitialized: false,
  isSdkInitializing: false,
  payMePaymentQRCodeUrl: null,
  setIsPaymentProcessing: (isPaymentProcessing: boolean) => set({ isPaymentProcessing }),
  setIsSdkInitialized: (isSdkInitialized: boolean) => set({ isSdkInitialized }),
  setIsSdkInitializing: (isSdkInitializing: boolean) => set({ isSdkInitializing }),
  setPayMePaymentQRCodeUrl: (payMePaymentQRCodeUrl: string | null) => set({ payMePaymentQRCodeUrl }),
}))

export const useFpCampaignPaymentService = (props: UseFpCampaignPaymentServiceArgs) => {
  const { campaignId, campaignCode, orderType, orderId, orderHash, onCreatePaymentTransactionError, paymentSuccessUrl, paymentFallbackUrl, countryCode } = props
  const [cartValues, setCartValues] = useFpCampaignCartStore(campaignId)
  const { destructuredData: campaign } = useApiCampaignActivityInfoImmutable(campaignCode)
  const { destructuredData: calculateResult, isLoading: isCalculateLoading } = useFpCampaignCartCalculate(campaignId, campaignCode)
  const { destructuredData: pendingOrder, isLoading: isLoadingPendingOrder } = useApiGetGuestPaymentTransactionRequest({ orderHash: orderHash || null })
  const { trigger: updatePaymentTransactionInvoice } = useApiUpdatePaymentTransactionInvoice(
    campaignCode && orderHash
      ? {
        code: campaignCode,
        order_hash: orderHash,
      }
      : undefined,
  )
  const { trigger: applePayMerchantValidation } = useApiApplePayMerchantValidation()
  const { resetCartValues } = useFpCampaignCartService(campaignId)

  const { trigger: createPaymentTransaction, isMutating: isCreatingOrder, error: createPaymentTransactionError } = useApiFpCretePaymentTransaction({ campaign_code: campaignCode }, {
    onError: (error) => {
      onCreatePaymentTransactionError?.(error)
    },
  })
  const { trigger: executePayment, isMutating: isExecutingPayment, error: executePaymentError } = useApiFpPaymentExecution()

  const isPendingOrder = !!pendingOrder
  const paymentMethods = useMemo(() => isPendingOrder ? pendingOrder.payment_methods : calculateResult?.payment_methods, [isPendingOrder, pendingOrder, calculateResult])
  const appliedPaymentMethod = useMemo(() => {
    return paymentMethods?.find(method => method.id === cartValues.payment.appliedPaymentMethodId)
  }, [cartValues.payment.appliedPaymentMethodId, paymentMethods])

  const isPaymentProcessing = useFpCampaignPaymentServiceStore(state => state.isPaymentProcessing)
  const isSdkInitialized = useFpCampaignPaymentServiceStore(state => state.isSdkInitialized)
  const isSdkInitializing = useFpCampaignPaymentServiceStore(state => state.isSdkInitializing)
  const setIsPaymentProcessing = useFpCampaignPaymentServiceStore(state => state.setIsPaymentProcessing)
  const setIsSdkInitializing = useFpCampaignPaymentServiceStore(state => state.setIsSdkInitializing)
  const setIsSdkInitialized = useFpCampaignPaymentServiceStore(state => state.setIsSdkInitialized)
  const payMePaymentQRCodeUrl = useFpCampaignPaymentServiceStore(state => state.payMePaymentQRCodeUrl)
  const setPayMePaymentQRCodeUrl = useFpCampaignPaymentServiceStore(state => state.setPayMePaymentQRCodeUrl)

  const isFlowEnabled = appliedPaymentMethod?.service_type === PaymentServiceType.CHECKOUTPAY

  const { destructuredData: appliedPaymentInfo, error: fpPaymentError, isValidating: isValidatingInitPayment } = useApiFpPayment({
    order_type: orderType,
    payment_method_id: appliedPaymentMethod?.id,
    order_id: orderType === ECommerceOrderType.CART ? calculateResult?.cart_uuid : orderId,
    remember_payment_card_token: cartValues.payment.rememberPaymentCardToken,
    installment: cartValues.payment.appliedInstallment ?? null,
    // 只在 applied method 走 Flow SDK 時帶 init payload 欄位（對齊 B2C `useWpPaymentService`）
    payment_success_url: isFlowEnabled ? paymentSuccessUrl : undefined,
    payment_fallback_url: isFlowEnabled ? paymentFallbackUrl : undefined,
    email: isFlowEnabled ? (cartValues.billing.billingAddressEmail || undefined) : undefined,
  }, {
    /** 因為後端是抓最後一次 fetch payment/payment，所以重複得資料也要重新 fetch */
    dedupingInterval: 0,
    revalidateOnFocus: false,
    disabled: isPaymentProcessing,
  })

  /**
   * 載入第三方付款模組
   */
  const isInitializingSdkRef = useRef(false)

  const initPaymentsSdk = useCallback(() => {
    if (isSdkInitialized || isSdkInitializing || isInitializingSdkRef.current || !paymentMethods) return

    isInitializingSdkRef.current = true
    console.debug('[useFpCampaignPaymentService] initPaymentsSdk started')

    setIsSdkInitializing(true)
    paymentService.initPaymentsSdk(paymentMethods)
      .then(() => {
        setIsSdkInitialized(true)
        console.debug('[useFpCampaignPaymentService] initPaymentsSdk success')
      })
      .catch((error) => {
        console.error('[useFpCampaignPaymentService] initPaymentsSdk error', error)
      })
      .finally(() => {
        isInitializingSdkRef.current = false
        setIsSdkInitializing(false)
      })
  }, [isSdkInitialized, isSdkInitializing, paymentMethods, setIsSdkInitialized, setIsSdkInitializing])

  // 代付款專用：dedupingInterval:0 讓每次 revalidate 都是新 session id，session guard 擋不住重複
  // config，重配會清掉輸入中的卡號。改記錄上次 config 的 module，只有換 module 才重配
  // （先選信用卡再切 Apple Pay 要能重配）。
  const configuredPendingOrderModuleRef = useRef<ReturnType<typeof getActivePaymentModuleB2B> | undefined>(undefined)
  const previousSessionIdRef = useRef<string | null>(null)

  const configPaymentModule = useCallback((appEnv: 'production' | 'sandbox', currency: string) => {
    if (!appliedPaymentInfo || !appEnv || !isSdkInitialized) return
    const currentSessionId = appliedPaymentInfo.payment_service_provider_info?.checkoutpay_session_info?.id
      ?? appliedPaymentInfo.payment_session?.id
      ?? null

    const paymentMethod = appliedPaymentInfo.payment_method
    if (!paymentMethod.service_type) return
    const paymentModule = getActivePaymentModuleB2B(paymentMethod.service_type, paymentMethod.type)
    if (!paymentModule) {
      console.error('[configPaymentModule]: Payment module is not found', appliedPaymentInfo)
      return
    }
    // 排在 session guard 前：module 沒換就早退（理由見上方 ref 宣告）
    if (orderHash && configuredPendingOrderModuleRef.current === paymentModule) return
    if (previousSessionIdRef.current !== null && previousSessionIdRef.current === currentSessionId) return
    previousSessionIdRef.current = currentSessionId
    const { app_id, app_key, app_pay_identifier } = appliedPaymentInfo.payment_service_provider_info
    try {
      // checkout pay 需要中載入 script 所以需要另外處理
      if (paymentModule instanceof CheckoutPayCreditCardModule && orderHash) {
        paymentModule.configurePayment({
          appId: String(app_id),
          appKey: String(app_key),
          appEnv,
          locale: countryCode ? CountryLocaleMap[countryCode] : undefined,
          shouldReinitialize: true,
        })
      }
      else if (!(paymentModule instanceof TappayApplePayModule)) {
        // Flow Apple Pay 也走這裡：金額與國別在 CKO session 內，前端帶的 Apple Pay 欄位會被忽略
        paymentModule.configurePayment({
          appId: String(app_id),
          appKey: String(app_key),
          appEnv,
          locale: countryCode ? CountryLocaleMap[countryCode] : undefined,
        })
      }
      else {
        // Tappay Apple Pay 前端要填金額：購物車讀試算、代付款讀訂單（購物車已清空）
        const applePayCountryCode = isPendingOrder ? pendingOrder.country_code : cartValues.billing.billingCountry
        const applePayTotalAmount = isPendingOrder ? pendingOrder.total : calculateResult?.total
        if (!applePayCountryCode || !applePayTotalAmount) return
        paymentModule.configurePayment({
          appId: String(app_id),
          appKey: String(app_key),
          appEnv,
          currency,
          countryCode: applePayCountryCode,
          orderTotalAmount: applePayTotalAmount,
          merchantIdentifier: String(app_pay_identifier),
          displayItems: [{
            label: '總計',
            amount: {
              currency,
              value: String(applePayTotalAmount),
            },
          }],
          onCancel: () => {
            alert('偵測到交易己取消，請前往「設定」確認Apple Pay及信用卡相關設定正確無誤。')
          },
          onValidateMerchant: (event, session) => {
            applePayMerchantValidation({ url: event.validationURL })
              .then((response) => {
                const data = response.data
                session.completeMerchantValidation(data)
                console.debug('[ApplePayModule] validate merchant complete', data)
              })
              .catch((error) => {
                session.abort()
                console.debug('[ApplePayModule] validate merchant error', error)
              })
          },
        })
      }
      if (orderHash) {
        configuredPendingOrderModuleRef.current = paymentModule
      }
    }
    catch (e) {
      console.error(e)
    }
  }, [applePayMerchantValidation, appliedPaymentInfo, calculateResult?.total, cartValues.billing.billingCountry, isPendingOrder, isSdkInitialized, orderHash, pendingOrder?.country_code, pendingOrder?.total])

  /**
   * 設定是否記住卡片
   */
  const setRememberPaymentCardToken = useCallbackEvent((rememberPaymentCardToken: boolean) => {
    setCartValues(state => ({
      ...state,
      payment: paymentService.setRememberPaymentCardToken(state.payment, rememberPaymentCardToken),
    }))
  })

  /**
   * 設定使用者選擇的卡號來源
   */
  const setCardSource = useCallbackEvent((cardSource: string) => {
    setCartValues(state => ({
      ...state,
      payment: paymentService.setCardSource(state.payment, cardSource),
    }))
  })

  /**
   * 設定使用者選擇的信用卡資訊
   */
  const setUserPaymentTokenInfo = useCallbackEvent((userPaymentTokenInfo: UserPaymentTokenInfo | null, isCreditCardPaymentMethod: boolean) => {
    setCartValues(state => ({
      ...state,
      payment: paymentService.setUserPaymentTokenInfo(state.payment, userPaymentTokenInfo, isCreditCardPaymentMethod),
    }))
  })

  /**
   * 清除使用者選擇的信用卡資訊
   */
  const resetUserPaymentTokenInfo = useCallbackEvent(() => {
    setCartValues(state => ({
      ...state,
      payment: paymentService.resetUserPaymentTokenInfo(state.payment),
    }))
  })

  /**
   * 設定使用者選擇的付款方式 id
   */
  const setAppliedPaymentMethodId = useCallbackEvent((appliedPaymentMethodId: number) => {
    setCartValues(state => ({
      ...state,
      payment: paymentService.setAppliedPaymentMethodId(state.payment, appliedPaymentMethodId),
    }))
  })

  /**
   * 如果使用者沒有選擇付款方式，則自動選擇第一個付款方式
   */
  const setDefaultPaymentMethod = useCallback(() => {
    if (cartValues.payment.appliedPaymentMethodId === null && paymentMethods && campaign?.payment_methods) {
      const defaultPaymentMethod = getDefaultPaymentMethod(campaign.payment_methods, paymentMethods)
      if (defaultPaymentMethod) {
        setAppliedPaymentMethodId(defaultPaymentMethod.id)
      }
    }
  }, [campaign?.payment_methods, cartValues.payment.appliedPaymentMethodId, paymentMethods, setAppliedPaymentMethodId])

  /**
   * 設定訂單付款交易 id
   */
  const setPaymentTransactionId = useCallbackEvent((paymentTransactionId: string) => {
    setCartValues(state => ({
      ...state,
      paymentTransaction: paymentTransactionService.setPaymentTransactionId(state.paymentTransaction, paymentTransactionId),
    }))
  })

  const setPaymentTransactionStatus = useCallbackEvent((paymentTransactionStatus: ECommercePaymentTransactionsStatus) => {
    setCartValues(state => ({
      ...state,
      paymentTransaction: paymentTransactionService.setPaymentTransactionStatus(state.paymentTransaction, paymentTransactionStatus),
    }))
  })
  const [isGettingPrime, setIsGettingPrime] = useState(false)
  const [getPrimeError, setGetPrimeError] = useState<unknown>()
  const getPaymentModulePrime = useCallbackEvent(async () => {
    const serviceType = appliedPaymentMethod?.service_type
    if (!serviceType) return
    const resolvedModule = getActivePaymentModuleB2B(serviceType, appliedPaymentMethod.type)
    if (isCkoFlowModule(resolvedModule)) {
      // throw 放在 try 外：下方 catch 會把錯誤吞成 null → prime 空照樣 execute，BE 收到無 token 的付款。
      // CKO Flow 走 dialog，不走 getPrime。
      if (resolvedModule.getPaymentExecutionMode() !== PAYMENT_EXECUTION_MODE.CKO_REDIRECT) {
        throw new Error('[useFpCampaignPaymentService] CKO Flow module must go through dialog flow, not getPrime')
      }
      // CKO_REDIRECT（如 AliPayHK）刻意不帶 token，BE 收到無 token 的 execute 後回 redirect_url
      return
    }
    try {
      setIsGettingPrime(true)
      setGetPrimeError(null)
      if (!resolvedModule) {
        if (requiresPaymentModule(appliedPaymentMethod.type)) throw new Error('Payment module is not found')
        return
      }
      const paymentModule = resolvedModule
      // NOTE: Local Tappay 無法 getPrime，請到 alpha, beta 測試
      const prime = await paymentService.getPrimeFromPaymentModule(paymentModule)
      if (!prime) throw new Error('Invalid prime')
      return prime
    }
    catch (error) {
      setGetPrimeError(error)
      console.error(error)
      return null
    }
  })

  const [isSuccessRedirect, setIsSuccessRedirect] = useState(false)
  const history = useHistory()
  const executePaymentProcess = useCallbackEvent(async (args: ExecutePaymentProcessArgs) => {
    const { payment_success_url, payment_fallback_url, orderId } = args
    if (!appliedPaymentInfo?.payment_transaction_id && !orderId) {
      throw new Error('Payment transaction id is required')
    }
    if (!cartValues.payment.appliedPaymentMethodId) {
      throw new Error('Payment method id is required')
    }
    const paymentTransactionId = appliedPaymentInfo?.payment_transaction_id || Number(orderId)
    const appliedPaymentMethodId = cartValues.payment.appliedPaymentMethodId
    const fullPaymentSuccessUrl = `${window.location.origin}${payment_success_url(args.orderHash)}`
    const fullPaymentFallbackUrl = `${window.location.origin}${payment_fallback_url(args.orderHash)}`

    try {
      // 只服務 token（TapPay / ECPay）；CKO Flow 走 dialog 分流，caller 已分流不會到這裡
      const prime = await getPaymentModulePrime()
      setIsGettingPrime(false)
      const { data: paymentResult } = await executePayment({
        payment_transaction_id: paymentTransactionId,
        service_pay_info: {
          bank_code: cartValues.payment.bankCode,
          apple_pay_info: prime?.applePayInfo,
          pay_token: prime?.paymentPrime,
          card_identifier: prime?.cardIdentifier,
        },
        user_payment_token_info: cartValues.payment.userPaymentTokenInfo,
        payment_success_url: fullPaymentSuccessUrl,
        payment_fallback_url: fullPaymentFallbackUrl,
        payment_option_id: appliedPaymentMethodId,
      })
      if (paymentResult.redirect_url) {
        if (isPayMeUrl(paymentResult.redirect_url)) {
          setPayMePaymentQRCodeUrl(paymentResult.redirect_url)
          return paymentResult.redirect_url
        }
        window.location.replace(paymentResult.redirect_url)
        setIsSuccessRedirect(true)
      }
      return undefined
    }
    catch (e) {
      history.push({ pathname: '', search: `?orderHash=${args.orderHash}` })
      console.error('executePaymentProcess', e)
      throw e
    }
  })
  const hasCreatePaymentTransaction = useRef(false)

  /** 建立購物車訂單並更新付款狀態，不執行付款。供 processCartPayment 與 CKO Flow Apple Pay dialog 共用。 */
  const createCampaignCartTransaction = useCallbackEvent(async (headers?: Record<string, string>) => {
    if (!appliedPaymentMethod?.type) {
      throw new Error('[createCampaignCartTransaction] Invalid payment method info')
    }
    const createPaymentTransactionDTO = new CreatePaymentTransactionDTO(appliedPaymentMethod.type)
    const homeDeliveryShippingMethodId = calculateResult?.shipping_methods.find(
      method => method.shipping_method_type === SHIPPING_METHOD_TYPE.HOME_DELIVERY,
    )?.shipping_method_id
    const appliedCoupons = calculateResult?.applied_coupons
    if (!homeDeliveryShippingMethodId) {
      throw new Error('[createCampaignCartTransaction] Applied shipping method is required')
    }
    const createPaymentRequest = createPaymentTransactionDTO.getRequest({
      ...cartValues,
      shipping: { appliedShippingMethodId: homeDeliveryShippingMethodId },
      appliedCoupons,
    })
    const { data: paymentTransaction } = await createPaymentTransaction({ ...createPaymentRequest, headers })
    hasCreatePaymentTransaction.current = true
    setPaymentTransactionId(paymentTransaction.data.order_hash)
    setPaymentTransactionStatus(paymentTransaction.data.status)
    return paymentTransaction.data
  })

  /**
   * 購物車結帳
   * 1. 建立訂單 await createCampaignCartTransaction(headers)
   * 2. 執行付款 await executePaymentProcess(rest)
   */
  const processCartPayment = useCallbackEvent(async (args: ProcessOrderPaymentArgs) => {
    try {
      const transaction = await createCampaignCartTransaction(args.headers as Record<string, string> | undefined)
      // resetCartValues 須在 executePaymentProcess 完成後才執行，
      // 否則 CKO Flow 付款進行中 paymentModule 會被提早 unmount。
      // PayMe QR 留在本頁等輪詢，orderHash 還要供輪詢與換碼使用，購物車改由 thankyou 頁清空。
      const paymentResult = await executePaymentProcess({ ...args, orderHash: transaction.order_hash })
      if (!paymentResult) resetCartValues()
    }
    catch (error) {
      console.error('processCartPayment', error)
      throw error
    }
    finally {
      setIsGettingPrime(false)
    }
  })

  /** 代付款送出前把這次填的發票寫回訂單；token 路線與 CKO Flow dialog 路線共用 */
  const updatePendingOrderInvoice = useCallbackEvent(async (orderId: string) => {
    if (!appliedPaymentMethod?.type) {
      throw new Error('Invalid payment method info')
    }
    const invoiceRequest = new CreatePaymentTransactionDTO(appliedPaymentMethod.type).getInvoiceRequest(cartValues)
    setPaymentTransactionId(orderId)
    await updatePaymentTransactionInvoice({
      invoice: invoiceRequest,
    })
  })

  /** 現有訂單結帳：先寫回發票再執行付款 */
  const processPendingOrderPayment = useCallbackEvent(async (props: ProcessPendingOrderPaymentArgs) => {
    const { orderId, orderHash, ...rest } = props
    try {
      if (!orderId) {
        throw new Error('Invalid payment method or orderId info')
      }
      await updatePendingOrderInvoice(orderId)
      await executePaymentProcess({ ...rest, orderHash, orderId })
    }
    catch (error) {
      console.error('processPendingOrderPayment', error)
      throw error
    }
  })

  const processPayment = useCallbackEvent((args: ProcessOrderPaymentArgs) => {
    if (!orderType) {
      throw new Error('Order type is required')
    }
    if (orderType === ECommerceOrderType.CART) {
      processCartPayment(args)
    }
    else {
      throw new Error('Invalid order type')
    }
  })

  const paymentMethodsGroupByType = useMemo(() => paymentService.getPaymentMethodsGroupByType(paymentMethods), [paymentMethods])

  // 用 appliedPaymentMethod（store 即時）而非 appliedPaymentInfo（API 延遲）推導 module，
  // 防切換付款方式後 API 尚未回前 fallback 回舊 module（Apple Pay→信用卡競態）。
  // ref fallback 僅保留給 resetCartValues 清空 appliedPaymentMethodId 時防 module 提早 unmount。
  const lastResolvedPaymentModuleRef = useRef<ReturnType<typeof getActivePaymentModuleB2B> | undefined>(undefined)
  const paymentModule = useMemo(() => {
    if (appliedPaymentMethod?.service_type) {
      const resolved = getActivePaymentModuleB2B(appliedPaymentMethod.service_type, appliedPaymentMethod.type)
      lastResolvedPaymentModuleRef.current = resolved
      return resolved
    }
    return lastResolvedPaymentModuleRef.current
  }, [appliedPaymentMethod?.service_type, appliedPaymentMethod?.type])

  const resetPaymentTransaction = useCallbackEvent(() => {
    setCartValues(state => ({
      ...state,
      paymentTransaction: defaultCartValues.paymentTransaction,
    }))
  })

  return {
    ...cartValues.payment,
    isValidatingInitPayment,
    isProcessing: isCreatingOrder || isExecutingPayment || isGettingPrime || isLoadingPendingOrder,
    errors: {
      createPaymentTransaction: createPaymentTransactionError,
      executePayment: executePaymentError,
      paymentError: fpPaymentError,
      getPrimeError,
    },
    appliedPaymentInfo,
    paymentMethodsGroupByType,
    setAppliedPaymentMethodId,
    setRememberPaymentCardToken,
    setUserPaymentTokenInfo,
    resetUserPaymentTokenInfo,
    setCardSource,
    processPayment,
    executePaymentProcess,
    payMePaymentQRCodeUrl,
    paymentTransactionId: cartValues.paymentTransaction.id,
    createCampaignCartTransaction,
    paymentModule,
    setDefaultPaymentMethod,
    configPaymentModule,
    processPendingOrderPayment,
    updatePendingOrderInvoice,
    initPaymentsSdk,
    isSdkInitialized,
    calculateResult,
    isCalculateLoading,
    hasCreatePaymentTransaction: hasCreatePaymentTransaction.current,
    isSuccessRedirect,
    resetPaymentTransaction,
    setIsPaymentProcessing,
    isPaymentProcessing,
  }
}
